import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { services } from "../data/mock";

export const Header = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/blog", label: "Blog" },
    { path: "/contact", label: "Contact" }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled
        ? "bg-white/98 shadow-md backdrop-blur-md"
        : "bg-white border-b border-brand-50"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[5.8rem]">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/nexfly-logo.png" alt="NexFLY Immigration Consultancy" className="h-[5.4rem] w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? "text-brand-500 bg-brand-50"
                    : "text-gray-600 hover:text-brand-500 hover:bg-brand-50/60"
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname.startsWith("/services")
                    ? "text-brand-500 bg-brand-50"
                    : "text-gray-600 hover:text-brand-500 hover:bg-brand-50/60"
                }`}
              >
                Services
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`} />
              </button>

              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[320px] bg-white rounded-2xl shadow-2xl shadow-brand-100/50 border border-brand-100/60 p-3 transition-all duration-200 origin-top ${
                isServicesOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
              }`}>
                <div className="grid grid-cols-1 gap-1">
                  {services.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-brand-50 transition-colors duration-150 group"
                      onClick={() => setIsServicesOpen(false)}
                    >
                      <div className="w-9 h-9 bg-brand-100 rounded-lg flex items-center justify-center group-hover:bg-brand-200 transition-colors duration-150 flex-shrink-0">
                        <span className="text-brand-500 text-xs font-bold">{service.title.charAt(0)}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-brand-700 transition-colors">{service.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {navLinks.slice(2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? "text-brand-500 bg-brand-50"
                    : "text-gray-600 hover:text-brand-500 hover:bg-brand-50/60"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="tel:+918248323674">
              <Button className="hidden sm:inline-flex bg-brand-500 hover:bg-brand-700 text-white rounded-xl px-5 h-10 text-sm font-medium shadow-lg shadow-brand-200/50 hover:shadow-brand-300/50 transition-all duration-300 hover:-translate-y-0.5">
                <Phone className="w-4 h-4 mr-2" />
                Enquire Now
              </Button>
            </a>

            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden rounded-xl hover:bg-brand-50">
                  <Menu className="w-5 h-5 text-gray-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0 border-l border-brand-100">
                <div className="p-6 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-8 pt-2">
                    <img src="/nexfly-logo.png" alt="NexFLY" className="h-14 w-auto" />
                  </div>

                  <nav className="space-y-1 flex-1 overflow-y-auto">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileOpen(false)}
                        className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                          isActive(link.path)
                            ? "text-brand-500 bg-brand-50"
                            : "text-gray-600 hover:bg-brand-50 hover:text-brand-500"
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}

                    <div className="pt-4 pb-2 px-4 text-xs font-semibold text-brand-400 uppercase tracking-widest">Services</div>
                    {services.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        onClick={() => setIsMobileOpen(false)}
                        className="block px-4 py-2.5 rounded-xl text-sm text-gray-500 hover:bg-brand-50 hover:text-brand-500 transition-colors"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </nav>

                  <div className="pt-6 border-t border-brand-50">
                    <a href="tel:+918248323674" onClick={() => setIsMobileOpen(false)}>
                      <Button className="w-full bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-11">
                        <Phone className="w-4 h-4 mr-2" />
                        Enquire Now
                      </Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
